import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, ExternalLink, FileText, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

const MESSAGES = {
  expired: "This link has expired.",
  revoked: "This link has been turned off.",
  invalid: "This link isn't valid.",
};

function Shell({ info, children }) {
  const color = info?.brand_color || "#18181b";
  return (
    <div className="min-h-screen bg-zinc-50 px-4 py-10" data-testid="public-resource-page">
      <div className="mx-auto max-w-lg rounded-xl border border-zinc-200 bg-white p-6 shadow-sm">
        <div className="mb-6 border-b-4 pb-4" style={{ borderColor: color }}>
          {info?.logo_url ? <img src={info.logo_url} alt={info.clinic_name} className="max-h-14 max-w-[240px]" />
            : <div className="text-xl font-bold text-zinc-900">{info?.clinic_name || "Your clinic"}</div>}
        </div>
        {children}
      </div>
    </div>
  );
}

export default function PublicResourceLink() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    fetch(`${API}/public/resource-links/${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) { setState("invalid"); return; }
        const d = await r.json();
        setInfo(d);
        setState(d.state);
      })
      .catch(() => setState("invalid"));
  }, [token]);

  if (state === "loading") return <Shell><Loader2 className="h-5 w-5 animate-spin text-zinc-400" /></Shell>;
  if (state !== "active") {
    const contact = [info?.contact_phone, info?.contact_email].filter(Boolean).join(" · ");
    return (
      <Shell info={info}>
        <h1 className="text-lg font-semibold text-zinc-900" data-testid="public-resource-unavailable">{MESSAGES[state] || MESSAGES.invalid}</h1>
        <p className="mt-2 text-sm text-zinc-600">Please contact the clinic and we&apos;ll send you a new one.{contact && <><br />{contact}</>}</p>
      </Shell>
    );
  }
  const fileUrl = `${API}/public/resource-links/${encodeURIComponent(token)}/file`;
  const inline = /pdf|image/.test(info.content_type || "");
  const color = info.brand_color || "#18181b";
  return (
    <Shell info={info}>
      <div className="flex items-start gap-3">
        <FileText className="mt-1 h-6 w-6 shrink-0 text-zinc-400" />
        <div>
          <h1 className="text-xl font-semibold text-zinc-900" data-testid="public-resource-title">{info.title}</h1>
          <p className="mt-1 text-sm text-zinc-600">Shared with you by {info.practitioner}.</p>
        </div>
      </div>
      {info.instructions && (
        <div className="mt-5 rounded-lg bg-zinc-50 p-4 text-sm text-zinc-800" data-testid="public-resource-instructions">
          <div className="mb-1 font-medium">Notes from your practitioner</div>
          <div className="whitespace-pre-wrap">{info.instructions}</div>
        </div>
      )}
      <div className="mt-6 flex flex-wrap gap-2">
        {inline && (
          <a href={fileUrl} target="_blank" rel="noopener noreferrer" data-testid="public-resource-open"
             className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-sm font-semibold" style={{ background: color, color: info.brand_text || "#fff" }}>
            <ExternalLink className="h-4 w-4" /> Open
          </a>
        )}
        <a href={`${fileUrl}?download=true`} data-testid="public-resource-download"
           className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 px-4 py-2.5 text-sm font-semibold text-zinc-800">
          <Download className="h-4 w-4" /> Download
        </a>
      </div>
      {(info.contact_phone || info.contact_email) && (
        <p className="mt-6 text-sm text-zinc-600">Questions? Contact {info.clinic_name}: {[info.contact_phone, info.contact_email].filter(Boolean).join(" · ")}</p>
      )}
      <p className="mt-3 text-xs text-zinc-500">
        This private link is just for you and expires on {new Date(info.expires_at).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}.
        If you have urgent or severe symptoms, seek medical help rather than waiting for a reply.
      </p>
    </Shell>
  );
}
